import { Service } from "@lib/titan2-slingo/dist/api/Service";
import { AbstractGameView } from "@lib/titan2-slingo/dist/game/view/abstract/display/AbstractGameView";
import { MouseBlocker } from "@lib/titan2/dist/game/control/MouseBlocker";
import { MouseBlockerProperties } from "@lib/titan2/dist/game/control/properties/MouseBlockerProperties";
import { TitanBitmapTextField } from "@lib/titan2/dist/game/control/TitanBitmapTextField";
import { TitanSpine } from "@lib/titan2/dist/game/control/TitanSpine";
import { TitanText } from "@lib/titan2/dist/game/control/TitanText";
import { ArrayUtil } from "@lib/titan2/dist/util/collection/ArrayUtil";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { MouseUtil } from "@lib/titan2/dist/util/view/MouseUtil";
import { SpineUtil } from "@lib/titan2/dist/util/view/SpineUtil";
import gsap from "gsap";
import { AlphaFilter, PI_2 } from "pixi.js";
import { ScatterPickEvent } from "../../../controller/event/GameEvent";
import { GameSoundEvent } from "../../../controller/event/GameSoundEvent";
import { TitanFancyTextFactory } from "../../../utils/FancyText/TitanFancyTextFactory";
import { GameSpineUtil } from "../../../utils/GameSpineUtil";
import { GameTextUtil } from "../../../utils/GameTextUtil";
import { TweenUtil } from "../../../utils/TweenUtil";
import { zip } from "../../../utils/zip";
export class GameScatterPickCoconut extends AbstractGameView {
    create() {
        super.create();
        Logger.addObjectRef(this, "GameScatterPickCoconut");
        this.coconutSpine = TitanSpine.createSpine(this, this.landscapeProperties.coconutSpineProperties, this.portraitProperties?.coconutSpineProperties);
        this.coconutSpine.cursor = "pointer";
        const ribbonPrizeSlots = this.coconutSpine.getSkeleton().slots.filter(slot => slot.data.name.startsWith("dynamic-slot-"));
        this.ribbonTexts = zip(this.landscapeProperties.ribbonPrizeTextProperties, this.portraitProperties?.ribbonPrizeTextProperties)
            .map(([landscapeProperties, portraitProperties]) => ribbonPrizeSlots.map(() => TitanText.createField(null, landscapeProperties, portraitProperties)));
        this.multiplierText = TitanBitmapTextField.createField(null, this.landscapeProperties.multiplierTextProperties, this.portraitProperties?.multiplierTextProperties);
        const miniTitleText = TitanFancyTextFactory.CreateElement(null, this.langManager, this.landscapeProperties.miniTitleTextProperties, this.portraitProperties?.miniTitleTextProperties);
        const minorTitleText = TitanFancyTextFactory.CreateElement(null, this.langManager, this.landscapeProperties.minorTitleTextProperties, this.portraitProperties?.minorTitleTextProperties);
        const majorTitleText = TitanFancyTextFactory.CreateElement(null, this.langManager, this.landscapeProperties.majorTitleTextProperties, this.portraitProperties?.majorTitleTextProperties);
        this.jackpotTitleTexts = [miniTitleText, minorTitleText, majorTitleText];
        this.jackpotMultiplierText = TitanBitmapTextField.createField(null, this.landscapeProperties.jackpotMultiplierTextProperties, this.portraitProperties?.jackpotMultiplierTextProperties);
        this.nextRibbonPrizeIndex = 0;
        const crackAnimations = this.coconutSpine.getSkeleton().data.animations.filter(animation => animation.name.startsWith("crack-"));
        this.crackIndexes = Array.from(crackAnimations.keys());
        this.jackpotTitleTexts.forEach(x => {
            if (x instanceof TitanText) {
                GameTextUtil.forceInitialise(x);
            }
        });
        GameTextUtil.forceInitialise(...this.ribbonTexts.flat(), this.multiplierText, this.jackpotMultiplierText);
        SpineUtil.addNamedEventListener(this.coconutSpine, "ribbonValue", this.populateNextRibbonPrize, this);
        SpineUtil.addNamedEventListener(this.coconutSpine, "fade-background", this.handleFadeOut, this, EXPLODE_TRACK);
        this.flash = new MouseBlocker(new MouseBlockerProperties(0xFFFFFF, 1, false));
    }
    postCreate() {
        super.postCreate();
        for (const ribbonText of this.ribbonTexts.flat()) {
            ribbonText.visible = false;
        }
        this.visible = false;
    }
    destroy() {
        super.destroy();
        this.coconutSpine.destroy();
        this.ribbonTexts.forEach(texts => texts.forEach(text => text.destroy()));
        this.multiplierText.destroy();
        this.jackpotTitleTexts.forEach(text => text.destroy());
        this.jackpotMultiplierText.destroy();
        this.flash.destroy();
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(ScatterPickEvent.TRANSITION_IN, this.handleTransitionIn);
        this.registerGame(ScatterPickEvent.PICK_REQUEST, this.handlePickRequest);
        this.registerGame(ScatterPickEvent.PICK_RESPONSE, this.handlePickResponse);
    }
    handleFadeOut() {
        this.dispatchGame(ScatterPickEvent.BACKGROUND_OUT);
    }
    async handleTransitionIn() {
        this.visible = true;
        this.suspenseCrackIndexes = this.getRandomSuspenseCrackIndexes();
        this.showText();
        const id = this.model.getScatterCount() - this.model.getScatterThreshold();
        this.coconutSpine.setSkin(`bonus-${id}`);
        // Fade the coconut spine in.
        gsap.killTweensOf(this.coconutSpine);
        this.coconutSpine.position.copyFrom(this.uiProperties.coconutSpineProperties.position);
        this.coconutSpine.stop();
        SpineUtil.playLoop(this.coconutSpine, "idle-loop", IDLE_LOOP_TRACK);
        SpineUtil.play(this.coconutSpine, "intensity", null, null, INTENSITY_TRACK);
        this.coconutSpine.getTrackEntry(INTENSITY_TRACK).alpha = 0;
        const alphaFilter = new AlphaFilter(0);
        this.coconutSpine.filters = [alphaFilter];
        await Promise.all([
            gsap.fromTo(alphaFilter, { alpha: 0 }, { alpha: 1, duration: 1.5, onComplete: () => this.coconutSpine.filters = null }),
            this.showRibbon(),
        ]);
    }
    async handlePickRequest() {
        if (Service.isReplay()) {
            // In replay, simulate a tap after a short delay.
            return new Promise(resolve => this.callAfter(1000, resolve));
        }
        // Wait for the coconut to be tapped by the player.
        MouseUtil.setInteractive(this.coconutSpine, true);
        this.coconutSpine.off("pointertap", this.handleIncidentalTap, this);
        await new Promise(resolve => this.coconutSpine.once("pointertap", resolve));
        this.coconutSpine.on("pointertap", this.handleIncidentalTap, this);
    }
    async handlePickResponse() {
        // The first pick also hides the ribbon.
        if (this.model.getPickTaps() === 1) {
            this.hideRibbon();
        }
        // Modulate the intensity per pick.
        this.playPickIntensity();
        // Create suspense until all taps are made.
        if (this.model.getPickTaps() < this.model.getPickTapsRequired()) {
            await this.playPickSuspense();
        }
        else {
            MouseUtil.setInteractive(this.coconutSpine, false);
            await this.playPickComplete();
        }
    }
    handleIncidentalTap() {
        // Give the coconut a very slight judder in a random direction.
        const randomDirection = PI_2 * Math.random();
        const dirX = Math.cos(randomDirection);
        const dirY = Math.sin(randomDirection);
        const x = dirX * this.uiProperties.judderDistance;
        const y = dirY * this.uiProperties.judderDistance;
        const angle = -this.uiProperties.judderAngle + this.uiProperties.judderAngle * 2 * Math.random();
        this.offsetAnimation?.seek(this.offsetAnimation.totalDuration());
        this.offsetAnimation = gsap.from(this.coconutSpine, {
            x: `+=${x}`,
            y: `+=${y}`,
            angle,
            ease: "elastic.out",
            duration: 0.5,
        });
    }
    handleRotate(event) {
        super.handleRotate(event);
        for (const ribbonTexts of this.ribbonTexts) {
            for (let i = 0; i < ribbonTexts.length; ++i) {
                GameSpineUtil.addToSlot(this.coconutSpine, ribbonTexts[i], `dynamic-slot-${ribbonTexts.length - i}`);
            }
        }
        for (const jackpotTitleText of this.jackpotTitleTexts) {
            GameSpineUtil.addToSlot(this.coconutSpine, jackpotTitleText, "dynamic-jackpot-title");
        }
        GameSpineUtil.addToSlot(this.coconutSpine, this.jackpotMultiplierText, "dynamic-jackpot-value");
        GameSpineUtil.addToSlot(this.coconutSpine, this.multiplierText, "dynamic-multiplier");
        TweenUtil.recover(this.offsetAnimation);
    }
    getActiveRibbonTexts() {
        const scatterThreshold = this.model.getScatterThreshold();
        const scatterCount = this.model.getScatterCount();
        const index = scatterCount - scatterThreshold - 1;
        return this.ribbonTexts[index];
    }
    getRandomSuspenseCrackIndexes() {
        // Distribute the crack indexes evenly(ish) across the suspense taps.
        const suspenseTapsRequired = this.model.getPickTapsRequired() - 1;
        const suspenseCrackIndexes = [];
        for (let i = 0; i < suspenseTapsRequired; ++i) {
            const tapCrackIndexes = [];
            for (let j = 0; j < this.crackIndexes.length; ++j) {
                if (j % suspenseTapsRequired === i) {
                    tapCrackIndexes.push(this.crackIndexes[j]);
                }
            }
            suspenseCrackIndexes.push(tapCrackIndexes);
        }
        ArrayUtil.shuffle(suspenseCrackIndexes, true);
        return suspenseCrackIndexes;
    }
    async showRibbon() {
        const activeRibbonTexts = this.getActiveRibbonTexts();
        activeRibbonTexts.forEach(text => text.alpha = 1);
        // Populate the ribbon texts with their first prizes.
        this.nextRibbonPrizeIndex = activeRibbonTexts.length;
        for (let i = 0; i < this.nextRibbonPrizeIndex; ++i) {
            const prize = this.getRibbonPrize(i);
            const prizeText = activeRibbonTexts[i];
            prizeText.setText(prize);
            prizeText.visible = true;
        }
        // Bring the ribbon in to a loop.
        SpineUtil.playLoop(this.coconutSpine, "ribbon-loop", RIBBON_LOOP_TRACK);
        await new Promise(resolve => SpineUtil.play(this.coconutSpine, "ribbon-in", resolve, null, RIBBON_TRANSITION_TRACK));
    }
    populateNextRibbonPrize() {
        const activeRibbonTexts = this.getActiveRibbonTexts();
        // Populate the next ribbon prize.
        const prize = this.getRibbonPrize(this.nextRibbonPrizeIndex);
        const prizeText = activeRibbonTexts[this.nextRibbonPrizeIndex % activeRibbonTexts.length];
        prizeText.setText(prize);
        // Advance the index for the next ribbon prize.
        ++this.nextRibbonPrizeIndex;
    }
    async hideRibbon() {
        // Animate the ribbon out, then remove the active ribbon texts from the coconut spine.
        const activeRibbonTexts = this.getActiveRibbonTexts();
        const animationDuration = this.coconutSpine.findAnimation("ribbon-out").duration;
        SpineUtil.play(this.coconutSpine, "ribbon-out", null, null, RIBBON_TRANSITION_TRACK);
        await gsap.to(activeRibbonTexts, { alpha: 0, duration: animationDuration });
        for (const ribbonText of activeRibbonTexts) {
            ribbonText.visible = false;
        }
    }
    getRibbonPrize(index) {
        // Wrap the index around the number of active prizes.
        const configId = this.model.getChanceGame().getGameConfig().getId();
        const activeScatterPrizes = this.model.getFeatureConfig().getChanceGameFeatureConfig().getMultipliers(configId);
        const prizeIndex = index % activeScatterPrizes.length;
        const prize = activeScatterPrizes[prizeIndex];
        return `${prize}x`;
    }
    playPickIntensity() {
        const intensityTrack = this.coconutSpine.getTrackEntry(INTENSITY_TRACK);
        const intensity = this.model.getPickTaps() / this.model.getPickTapsRequired();
        gsap.to(intensityTrack, { alpha: intensity, duration: 0.7, ease: "back.inOut(5)" });
    }
    async playPickSuspense() {
        const tapIndex = this.model.getPickTaps() - 1;
        const crackIndexes = this.suspenseCrackIndexes[tapIndex];
        const cracks = crackIndexes.map(index => SpineUtil.play(this.coconutSpine, `crack-${index + 1}`, null, null, CRACK_TRACK + index));
        const reaction = new Promise(resolve => SpineUtil.play(this.coconutSpine, "react", resolve, null, REACT_TRACK));
        this.dispatchGame(GameSoundEvent.COCONUT_CRACK);
        await Promise.all([...cracks, reaction]);
    }
    async playPickComplete() {
        // Clear the cracks so they don't linger on top of the explosion (which contains them baked in).
        for (const index of this.crackIndexes) {
            this.coconutSpine.clearTrack(CRACK_TRACK + index);
            for (const spine of GameSpineUtil.orientations(this.coconutSpine)) {
                spine.state.clearTrack(CRACK_TRACK + index);
            }
        }
        // Move the coconut to the centre of the screen.
        this.offsetAnimation?.kill();
        this.offsetAnimation = gsap.to(this.coconutSpine, { y: 0, duration: 0.6 });
        // Explode the coconut.
        this.dispatchGame(GameSoundEvent.COCONUT_EXPLODE);
        await new Promise(resolve => {
            SpineUtil.play(this.coconutSpine, "explode", resolve, null, EXPLODE_TRACK);
            const flashEventTime = GameSpineUtil.getEventTime(this.coconutSpine, "explosion-flash", EXPLODE_TRACK);
            const flashInDuration = 0.25;
            const flashOutDuration = 0.4;
            this.addChild(this.flash);
            this.flash.alpha = 0;
            gsap
                .timeline({
                onUpdate: () => this.flash.fitToWindow(),
                onComplete: () => this.removeChild(this.flash),
            })
                .to(this.flash, { alpha: 1, duration: flashInDuration, ease: "power1.in", delay: flashEventTime - flashInDuration })
                .to(this.flash, { duration: flashInDuration })
                .call(() => {
                this.dispatchGame(ScatterPickEvent.COCONUT_FLASH);
                this.dispatchGame(GameSoundEvent.COCONUT_EXPLODE_REVEAL);
            })
                .to(this.flash, { alpha: 0, duration: flashOutDuration, ease: "power1.out" });
        });
        this.visible = false;
    }
    showText() {
        // Use the appropriate label text based on the scatter win type.
        const gameId = this.model.getChanceGame()?.getGameConfig().getId();
        const gameMultipliers = this.model.getFeatureConfig().getChanceGameFeatureConfig().getMultipliers(gameId);
        const multiplier = this.model.getChanceGame()?.getParams().getMultiplier() ?? 1;
        const multiplierMessage = `${multiplier}x`;
        const multiplierIndex = gameMultipliers.indexOf(multiplier);
        const titleIndex = this.jackpotTitleTexts.length - (gameMultipliers.length - multiplierIndex);
        console.log(`Multiplier: ${multiplier}, Index: ${multiplierIndex}, Title Index: ${titleIndex}`);
        if (titleIndex >= 0) {
            this.jackpotMultiplierText.setText(multiplierMessage);
            this.jackpotMultiplierText.visible = true;
            this.multiplierText.visible = false;
            for (let i = 0; i < this.jackpotTitleTexts.length; ++i) {
                this.jackpotTitleTexts[i].visible = titleIndex === i;
            }
        }
        else {
            this.multiplierText.setText(multiplierMessage);
            this.multiplierText.visible = true;
            this.jackpotMultiplierText.visible = false;
            for (const jackpotTitleText of this.jackpotTitleTexts) {
                jackpotTitleText.visible = false;
            }
        }
    }
    showMultiplierText() {
        const multiplier = this.model.getChanceGame()?.getParams().getMultiplier() ?? 1;
        const multiplierMessage = `${multiplier}x`;
        this.multiplierText.setText(multiplierMessage);
        this.jackpotMultiplierText.setText(multiplierMessage);
    }
}
const IDLE_LOOP_TRACK = 0;
const RIBBON_LOOP_TRACK = 2;
const RIBBON_TRANSITION_TRACK = 1;
const REACT_TRACK = 3;
const EXPLODE_TRACK = 4;
const INTENSITY_TRACK = 5;
const CRACK_TRACK = 6;
//# sourceMappingURL=GameScatterPickCoconut.js.map